import React from 'react';
import NotificationDeepLinkPicker from './NotificationDeepLinkPicker';
import WhatsAppSearchSelect from './WhatsAppSearchSelect';
import { mergeNotificationFields, notificationFieldSelectOptions } from './whatsappNotificationFields';
import type { WhatsAppContextField, WhatsAppTemplateVariableMapping } from '../../models/MasterAdmin/whatsappHub';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';

export default function WhatsAppTemplateVariableMappingEditor({
  mappings,
  fields,
  campaigns,
  onCampaignCreated,
  onCampaignUpdated,
  onRefreshCampaigns,
  onChange,
  compact = false,
}: {
  mappings: WhatsAppTemplateVariableMapping[];
  fields: WhatsAppContextField[];
  campaigns: DeepLinkCampaign[];
  onCampaignCreated: (c: DeepLinkCampaign) => void;
  onCampaignUpdated?: (c: DeepLinkCampaign) => void;
  onRefreshCampaigns?: () => void;
  onChange: (next: WhatsAppTemplateVariableMapping[]) => void;
  compact?: boolean;
}) {
  const allFields = React.useMemo(() => mergeNotificationFields(fields), [fields]);
  const fieldByKey = React.useMemo(() => new Map(allFields.map((f) => [f.key, f])), [allFields]);
  const fieldOptions = React.useMemo(
    () => notificationFieldSelectOptions(allFields, mappings.map((m) => m.contextKey)),
    [allFields, mappings]
  );

  const updateRow = (slot: number, patch: Partial<WhatsAppTemplateVariableMapping>) => {
    onChange(mappings.map((m) => (m.slot === slot ? { ...m, ...patch } : m)));
  };

  if (mappings.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        Add body placeholders like {'{{1}}'}, {'{{2}}'} to configure variable mappings.
      </p>
    );
  }

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      <p className="text-xs text-gray-500">
        Map each placeholder ({'{{1}}'}, {'{{2}}'}, …) to notification data, static text, or a deep link campaign.
        Ref numbers in Settings are a guide only — slot order is what Meta uses in this template.
      </p>
      {mappings.map((m) => (
        <div key={m.slot} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <div className="mb-2 flex items-center gap-2">
            <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs dark:bg-gray-800">{`{{${m.slot}}}`}</span>
            <input
              type="text"
              value={m.label}
              onChange={(e) => updateRow(m.slot, { label: e.target.value })}
              placeholder="Label (e.g. Doctor name)"
              className="flex-1 rounded border px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800"
            />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <select
              value={m.sourceType}
              onChange={(e) => {
                const sourceType = e.target.value as WhatsAppTemplateVariableMapping['sourceType'];
                const patch: Partial<WhatsAppTemplateVariableMapping> = {
                  sourceType,
                  contextKey: sourceType === 'context' ? m.contextKey : null,
                  deepLinkCampaignId: sourceType === 'deep_link' ? m.deepLinkCampaignId : null,
                };
                if (sourceType === 'deep_link') {
                  patch.label = 'Deep link';
                } else if (sourceType === 'static') {
                  patch.label = 'Static value';
                } else if (m.contextKey) {
                  patch.label = fieldByKey.get(m.contextKey)?.label || m.label;
                }
                updateRow(m.slot, patch);
              }}
              className="rounded border px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
            >
              <option value="context">Notification field</option>
              <option value="static">Static text</option>
              <option value="deep_link">Deep link campaign</option>
            </select>
            {m.sourceType === 'static' ? (
              <input
                type="text"
                value={m.staticValue || ''}
                onChange={(e) => updateRow(m.slot, { staticValue: e.target.value })}
                placeholder="Static value"
                className="rounded border px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
              />
            ) : m.sourceType === 'deep_link' ? (
              <span className="self-center text-xs text-gray-400">Configure below</span>
            ) : (
              <WhatsAppSearchSelect
                value={m.contextKey || ''}
                onChange={(contextKey) => {
                  const field = contextKey ? fieldByKey.get(contextKey) : undefined;
                  updateRow(m.slot, {
                    contextKey: contextKey || null,
                    label: field?.label || m.label,
                  });
                }}
                options={fieldOptions}
                placeholder="Select notification field…"
                emptyLabel="Select notification field…"
                searchPlaceholder="Search fields…"
              />
            )}
          </div>
          {m.sourceType === 'deep_link' && (
            <NotificationDeepLinkPicker
              value={{
                deepLinkCampaignId: m.deepLinkCampaignId,
                deepLinkUrlType: m.deepLinkUrlType,
                deepLinkParamBindings: m.deepLinkParamBindings,
              }}
              onChange={(next) =>
                updateRow(m.slot, {
                  deepLinkCampaignId: next.deepLinkCampaignId,
                  deepLinkUrlType: next.deepLinkUrlType,
                  deepLinkParamBindings: next.deepLinkParamBindings,
                })
              }
              contextFields={allFields}
              campaigns={campaigns}
              onCampaignCreated={onCampaignCreated}
              onCampaignUpdated={onCampaignUpdated}
              onRefreshCampaigns={onRefreshCampaigns}
            />
          )}
        </div>
      ))}
    </div>
  );
}
